import type { ImportWorkerFormValues } from "../types";

export const SINGLE_FILE_FIELDS = [
  "photo_file",
  "national_id_file",
  "military_certificate_file",
  "bank_certificate_file",
  "enrollment_verification_file",
  "work_proof_file",
  "enpf_certificate_file",
  "contract_file",
  "job_application_file",
  "order_on_hiring_file",
  "personal_sheet_file",
] as const;

export const MULTI_FILE_FIELDS = ["diploma_file", "diploma_transcript_file", "social_category_document_file"] as const;

export function formatDateToApi(date: Date | null): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function appendTimeSeconds(time: string): string {
  if (!time) return "";
  if (time.length === 5) return `${time}:00`;
  return time;
}

export function extractScalarData(values: ImportWorkerFormValues): Record<string, unknown> {
  const scalar: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value instanceof File) continue;
    if (Array.isArray(value) && value.some((v) => v instanceof File)) continue;
    if (value instanceof Date) {
      scalar[key] = value.toISOString();
    } else {
      scalar[key] = value;
    }
  }
  return scalar;
}

export function buildImportFormData(data: ImportWorkerFormValues): FormData {
  const fd = new FormData();

  fd.append("iin", data.iin);
  fd.append("surname", data.surname);
  fd.append("name", data.name);
  if (data.father_name) fd.append("father_name", data.father_name);
  fd.append("date_of_birth", formatDateToApi(data.date_of_birth));
  fd.append("gender", data.gender);
  fd.append("nationality", data.nationality);
  if (data.citizenship) fd.append("citizenship", data.citizenship);
  fd.append("is_resident", data.is_resident ? "true" : "false");
  fd.append("is_student", data.is_student ? "true" : "false");
  if (data.is_student && data.enrollment_start_date) {
    fd.append("enrollment_start_date", formatDateToApi(data.enrollment_start_date));
  }
  if (data.is_student && data.enrollment_end_date) {
    fd.append("enrollment_end_date", formatDateToApi(data.enrollment_end_date));
  }
  fd.append("has_no_education", data.has_no_education ? "true" : "false");
  fd.append("has_no_work_experience", data.has_no_work_experience ? "true" : "false");
  if (data.family_status) fd.append("family_status", data.family_status);
  fd.append("email", data.email);
  fd.append("phone", data.phone);

  fd.append("sign_date", formatDateToApi(data.sign_date));
  fd.append("start_date", formatDateToApi(data.start_date));
  if (data.contract_number) fd.append("contract_number", data.contract_number);
  if (data.work_city_id != null) fd.append("work_city_id", String(data.work_city_id));
  if (data.employee_address_ru) fd.append("employee_address_ru", data.employee_address_ru);
  if (data.employee_address_kk) fd.append("employee_address_kk", data.employee_address_kk);
  fd.append("job_position_ru", data.job_position_ru);
  fd.append("job_position_kk", data.job_position_kk);
  fd.append("job_duties_ru", JSON.stringify(data.job_duties_ru.filter(Boolean)));
  fd.append("job_duties_kk", JSON.stringify(data.job_duties_kk.filter(Boolean)));
  fd.append("working_days_list", JSON.stringify(data.working_days_list));
  if (data.work_start_time) fd.append("work_start_time", appendTimeSeconds(data.work_start_time));
  if (data.work_end_time) fd.append("work_end_time", appendTimeSeconds(data.work_end_time));
  fd.append("has_break", data.has_break ? "true" : "false");
  if (data.has_break && data.break_start_time) fd.append("break_start_time", appendTimeSeconds(data.break_start_time));
  if (data.has_break && data.break_end_time) fd.append("break_end_time", appendTimeSeconds(data.break_end_time));
  if (data.work_format) fd.append("work_format", data.work_format);
  fd.append("salary_amount", data.salary_amount);
  fd.append("trial_period", data.trial_period ? "true" : "false");
  if (data.trial_period && data.trial_duration_months != null) {
    fd.append("trial_duration_months", String(data.trial_duration_months));
  }
  if (data.current_vacation_days) fd.append("current_vacation_days", data.current_vacation_days);

  fd.append("bank_name", data.bank_name);
  fd.append("iban_account", data.iban_account);
  fd.append("bik_number", data.bik_number);

  for (const field of SINGLE_FILE_FIELDS) {
    const file = data[field];
    if (file instanceof File) {
      fd.append(field, file);
    }
  }

  for (const field of MULTI_FILE_FIELDS) {
    const files = data[field];
    if (Array.isArray(files)) {
      for (const file of files) {
        if (file instanceof File) {
          fd.append(field, file);
        }
      }
    }
  }

  return fd;
}
